// /app/frontend/src/components/frugal-v2/tabs/execution/CategoryRow.jsx
// Single category row with expandable transactions (100 lines)

import React from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, RefreshCw, Mail, CreditCard, Loader2 } from 'lucide-react';
import { getBudgetStatusColor } from '../../constants/colors';
import { VITAL_CATEGORIES, translateCategory } from '../../constants/categories';

const SourceIcon = ({ source }) => {
  switch (source) {
    case 'email': return <Mail className="w-3 h-3 text-sky-400" />;
    case 'recurring': return <RefreshCw className="w-3 h-3 text-amber-400" />;
    default: return <CreditCard className="w-3 h-3 text-slate-400" />;
  }
};

export default function CategoryRow({ 
  cat, isExpanded, onToggle, transactions, loadingTransactions, categoryColor, t 
}) {
  const isVital = VITAL_CATEGORIES.includes(cat.category);
  const isOverBudget = cat.percent > 100;
  const { bg, bar, text } = getBudgetStatusColor(cat.percent);

  return (
    <div className={`bg-white/5 rounded-xl border ${isOverBudget ? 'border-red-500/30' : 'border-white/5'}`}>
      {/* Main Row */}
      <button onClick={() => onToggle(cat.category)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: categoryColor }} />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium">{translateCategory(t, cat.category)}</span>
              {isVital && <span className="text-xs text-sky-400">({t('frugal.budget.vital')})</span>}
              {isOverBudget && <AlertTriangle className="w-4 h-4 text-red-400" />}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              ${cat.spent.toFixed(2)} / ${cat.budget.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${text}`}>{cat.percent.toFixed(0)}%</span>
          {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {/* Progress Bar */}
      <div className="px-4 pb-3">
        <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
          <div className={`h-full ${bar} transition-all`} style={{ width: `${Math.min(cat.percent, 100)}%` }} />
        </div>
      </div>

      {/* Expanded Transactions */}
      {isExpanded && (
        <div className="border-t border-white/10 p-3 space-y-2">
          {loadingTransactions === cat.category ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
            </div>
          ) : transactions?.length === 0 ? (
            <div className="text-center py-4 text-slate-500 text-sm">
              {t('frugal.execution.noTransactions')}
            </div>
          ) : (
            transactions?.map((tx, idx) => (
              <div key={tx.id || idx} className="flex items-center justify-between py-2 px-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <SourceIcon source={tx.source} />
                  <div>
                    <div className="text-sm text-white">{tx.description || tx.name}</div>
                    <div className="text-xs text-slate-500">{tx.date || tx.created_at?.split('T')[0]}</div>
                  </div>
                </div>
                <span className="text-emerald-400 font-medium">-${tx.amount?.toFixed(2)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
